import { useEffect, useId, useMemo, useReducer, useRef, useState } from 'react'
import type { CSSProperties, MouseEvent } from 'react'
import { defaultWallets } from '../data/wallets'
import { useWallet } from '../context/WalletProvider'
import type { Wallet, WalletConnection, WalletFormFields, WalletModalProps } from '../types'
import { WalletConnecting, WalletError, WalletManualForm, WalletNextStep } from './WalletModalSteps'
import './WalletModal.css'

type Flow =
  | { step: 'idle'; selectedWallet: null }
  | { step: 'connecting'; selectedWallet: Wallet }
  | { step: 'next-step'; selectedWallet: Wallet; connection: WalletConnection }
  | { step: 'error'; selectedWallet: Wallet; message: string }
  | { step: 'manual-form'; selectedWallet: Wallet; message?: string }

type FlowEvent =
  | { type: 'START'; wallet: Wallet }
  | { type: 'CONNECTED'; wallet: Wallet; connection: WalletConnection }
  | { type: 'FAILED'; wallet: Wallet; message: string }
  | { type: 'MANUAL'; wallet: Wallet; message?: string }
  | { type: 'RESET' }

const initialFlow: Flow = { step: 'idle', selectedWallet: null }
const MIN_PROVIDER_LOADING_MS = 700

function flowReducer(_flow: Flow, event: FlowEvent): Flow {
  switch (event.type) {
    case 'START':
      return {
        step: 'connecting',
        selectedWallet: event.wallet
      }

    case 'CONNECTED':
      return {
        step: 'next-step',
        selectedWallet: event.wallet,
        connection: event.connection
      }

    case 'FAILED':
      return {
        step: 'error',
        selectedWallet: event.wallet,
        message: event.message
      }

    case 'MANUAL':
      return {
        step: 'manual-form',
        selectedWallet: event.wallet,
        message: event.message
      }

    case 'RESET':
      return initialFlow
  }
}

function WalletMark({ wallet }: { wallet: Wallet }) {
  const [failed, setFailed] = useState(false)

  return (
    <span
      className={`wallet-mark${wallet.icon && !failed ? ' wallet-mark--icon' : ''}`}
      style={{ '--wallet-color': wallet.color } as CSSProperties}
      aria-hidden="true"
    >
      {wallet.icon && !failed ? (
        <img
          src={wallet.icon}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <span>{wallet.mark}</span>
      )}
    </span>
  )
}

export function WalletModal({
  open,
  onClose,
  wallets = defaultWallets,
  branding = {},
  theme = 'dark',
  accentColor = '#c4f579',
  onConnected,
  onFormSubmit
}: WalletModalProps) {
  const { connect, connectManually } = useWallet()

  const titleId = useId()
  const searchId = useId()

  const dialogRef = useRef<HTMLElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const closeRef = useRef(onClose)

  const attemptRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const delayResolverRef = useRef<(() => void) | null>(null)

  const [query, setQuery] = useState('')
  const [flow, dispatch] = useReducer(flowReducer, initialFlow)

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase()

    if (!normalized) return wallets

    return wallets.filter((wallet) =>
      `${wallet.name} ${wallet.description ?? ''} ${wallet.aliases.join(' ')} ${wallet.chains.join(' ')}`
        .toLowerCase()
        .includes(normalized)
    )
  }, [wallets, query])

  const appName = branding.appName ?? 'Orbit'

  const cancelPending = () => {
    attemptRef.current += 1

    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
    }

    timerRef.current = null

    const resolveDelay = delayResolverRef.current
    delayResolverRef.current = null

    resolveDelay?.()
  }

  const closeModal = () => {
    cancelPending()
    onClose()
  }

  useEffect(() => {
    closeRef.current = closeModal
  })

  useEffect(() => {
    if (!open) return

    dispatch({ type: 'RESET' })
    setQuery('')

    const previous = document.activeElement as HTMLElement | null

    const timer = window.setTimeout(() => {
      searchRef.current?.focus()
    }, 60)

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeRef.current()
        return
      }

      if (event.key === 'Tab' && dialogRef.current) {
        const nodes =
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not(:disabled),input:not(:disabled),a[href]'
          )

        if (!nodes.length) return

        const first = nodes[0]
        const last = nodes[nodes.length - 1]

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('keydown', onKeyDown)

      cancelPending()
      previous?.focus()
    }
  }, [open])

  const startConnection = async (wallet: Wallet) => {
    cancelPending()

    const attempt = attemptRef.current

    dispatch({
      type: 'START',
      wallet
    })

    const startedAt = Date.now()

    const holdLoadingBriefly = async () => {
      const remaining =
        MIN_PROVIDER_LOADING_MS - (Date.now() - startedAt)

      if (remaining <= 0) return

      await new Promise<void>((resolve) => {
        const finish = () => {
          timerRef.current = null

          if (delayResolverRef.current === finish) {
            delayResolverRef.current = null
          }

          resolve()
        }

        delayResolverRef.current = finish
        timerRef.current = window.setTimeout(finish, remaining)
      })
    }

    try {
      const connection = await connect(
        wallet.adapterId ?? wallet.id,
        wallet
      )

      onConnected?.(connection)

      await holdLoadingBriefly()

      if (attemptRef.current === attempt) {
        dispatch({
          type: 'CONNECTED',
          wallet,
          connection
        })
      }
    } catch (cause) {
      await holdLoadingBriefly()

      if (attemptRef.current !== attempt) return

      dispatch({
        type: 'FAILED',
        wallet,
        message:
          cause instanceof Error
            ? cause.message
            : 'Connection failed. Please try again.'
      })
    }
  }


  const startManualConnection = async (
    wallet: Wallet,
    fields: WalletFormFields
  ) => {
    const { connectionMode } = fields
    cancelPending()

    const attempt = attemptRef.current

    dispatch({
      type: 'START',
      wallet
    })

    try {
      await onFormSubmit?.({ walletId: wallet.id, walletName: wallet.name, ...fields })
      if (attemptRef.current !== attempt) return

      const connection = await connectManually(
        wallet.adapterId ?? wallet.id,
        wallet,
        { connectionMode }
      )

      if (attemptRef.current !== attempt) return

      onConnected?.(connection)

      dispatch({
        type: 'CONNECTED',
        wallet,
        connection
      })
    } catch (cause) {
      if (attemptRef.current !== attempt) return

      dispatch({
        type: 'FAILED',
        wallet,
        message:
          cause instanceof Error
            ? cause.message
            : 'Manual connection failed. Please try again.'
      })
    }
  }

  const backdrop = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      closeModal()
    }
  }

  if (!open) return null

  return (
    <div
      className="wallet-modal-backdrop"
      data-theme={theme}
      style={{ '--wallet-accent': accentColor } as CSSProperties}
      onMouseDown={backdrop}
    >
      <section
        className="wallet-modal"
        data-step={flow.step}
        role="dialog"
        aria-modal="true"
        aria-label={
          flow.step === 'idle'
            ? undefined
            : flow.step === 'connecting'
              ? `Connecting to ${flow.selectedWallet.name}`
              : flow.step === 'error'
                ? `Connection failed for ${flow.selectedWallet.name}`
                : flow.step === 'manual-form'
                  ? `Manual connection for ${flow.selectedWallet.name}`
                  : `Connected to ${flow.selectedWallet.name}`
        }
        aria-labelledby={flow.step === 'idle' ? titleId : undefined}
        ref={dialogRef}
      >
        <header className="wallet-modal__header">
          <div className="wallet-modal__brand">
            {branding.appIcon ? (
              <img
                src={branding.appIcon}
                alt=""
                className="wallet-modal__app-icon"
              />
            ) : (
              <span
                className="wallet-modal__brand-mark"
                aria-hidden="true"
              >
                ✳
              </span>
            )}

            <span>{appName}</span>
            <span className="wallet-modal__divider">/</span>
            <span className="wallet-modal__section">
              Connect wallet
            </span>
          </div>

          <button
            className="wallet-modal__close"
            onClick={closeModal}
            aria-label="Close wallet selector"
          >
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="m5 5 10 10M15 5 5 15" />
            </svg>
          </button>
        </header>

        <div className="wallet-modal__content">
          <div className="wallet-page" key={flow.step}>
            {flow.step === 'idle' && (
              <>
                <div className="wallet-modal__intro">
                  <p className="wallet-modal__eyebrow">
                    YOUR WALLET, YOUR CHOICE
                  </p>

                  <h2 id={titleId}>Connect a wallet</h2>

                  <p className="wallet-modal__description">
                    Choose a wallet to securely connect to {appName}.
                  </p>
                </div>

                <label
                  className="wallet-search"
                  htmlFor={searchId}
                >
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <circle cx="8.7" cy="8.7" r="5.8" />
                    <path d="m13 13 4 4" />
                  </svg>

                  <input
                    ref={searchRef}
                    id={searchId}
                    type="search"
                    placeholder="Search wallets"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    autoComplete="off"
                  />

                  {query && (
                    <button
                      type="button"
                      className="wallet-search__clear"
                      onClick={() => setQuery('')}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </label>

                <div
                  className="wallet-grid"
                  role="list"
                  aria-label="Available wallets"
                >
                  {filtered.map((wallet) => (
                    <button
                      key={wallet.id}
                      className="wallet-option"
                      onClick={() => void startConnection(wallet)}
                      aria-label={`Choose ${wallet.name}`}
                    >
                      <WalletMark wallet={wallet} />
                      <span className="wallet-option__name">
                        {wallet.name}
                      </span>
                    </button>
                  ))}

                  {filtered.length === 0 && (
                    <div className="wallet-empty">
                      <span aria-hidden="true">⌕</span>
                      <strong>No wallets found</strong>
                      <p>Try another wallet name.</p>
                    </div>
                  )}
                </div>

                <p className="wallet-modal__footnote">
                  New to wallets?{' '}
                  <a
                    href="https://ethereum.org/wallets/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Learn what a wallet is{' '}
                    <span aria-hidden="true">↗</span>
                  </a>
                </p>
              </>
            )}

            {flow.step === 'connecting' && (
              <WalletConnecting
                wallet={flow.selectedWallet}
                WalletMark={WalletMark}
              />
            )}

            {flow.step === 'next-step' && (
              <WalletNextStep
                wallet={flow.selectedWallet}
                connection={flow.connection}
                demo={false}
                onDone={closeModal}
                WalletMark={WalletMark}
              />
            )}

            {flow.step === 'error' && (
              <WalletError
                wallet={flow.selectedWallet}
                message={flow.message}
                onRetry={() =>
                  void startConnection(flow.selectedWallet)
                }
                onManual={() =>
                  dispatch({
                    type: 'MANUAL',
                    wallet: flow.selectedWallet
                  })
                }
                WalletMark={WalletMark}
              />
            )}

            {flow.step === 'manual-form' && (
              <WalletManualForm
                wallet={flow.selectedWallet}
                onBack={() =>
                  dispatch({
                    type: 'FAILED',
                    wallet: flow.selectedWallet,
                    message: flow.message ?? 'Connection failed.'
                  })
                }
                onSubmit={(fields) =>
                  void startManualConnection(flow.selectedWallet, fields)
                }
                WalletMark={WalletMark}
              />
            )}
          </div>
        </div>

        <footer className="wallet-modal__footer">
          <span>Secured by your wallet</span>
        </footer>
      </section>
    </div>
  )
}
